import { yupResolver } from "@hookform/resolvers/yup";
import { Alert } from "@omni/ui/components/Alert/alert.component";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { FormProvider } from "react-hook-form";
import { useSelector } from "react-redux";

import { FormGroup } from "@/components/Form/Base/formGroup.component";
import { Input } from "@/components/Form/Base/input.component";
import { Label } from "@/components/Form/Base/label.component";
import { GiftCardField } from "@/components/Form/RHF/FormFields/GiftCardField/giftCardField.component";
import { NumericTextField } from "@/components/Form/RHF/FormFields/NumericTextField/NumericTextfield.component";
import { RecaptchaField } from "@/components/Form/RHF/FormFields/RecaptchaField/recaptchaField.component";
import { useOmniForm } from "@/components/Form/RHF/Utils/useOmniForm";
import { Col } from "@/components/General/Grid/col.component";
import { Row } from "@/components/General/Grid/row.component";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { getErrorMessage } from "@/utils/errors";
import { FormInfoProvider } from "@/utils/forms/formInfo.provider";
import { scrollToTop } from "@/utils/scroll";
import { BONUSCARD_DISCOUNT_PREFIX } from "@/utils/validation/config";

import {
    CheckoutGiftCardFormValidationSchema,
    CheckoutGiftCardFormValues
} from "./giftCardForm.schema";
import GiftCardList from "./giftCardList/giftCardList.component";

type Props = {
    onSubmit: (
        values: CheckoutGiftCardFormValues,
        resetFormCallback: () => void
    ) => void;
    onDelete: (cardCode: string) => Promise<void>;
    giftCardList: Array<any>;
    errorMessage?: string | null;
    isLoading: boolean;
    renderInput?: boolean;
    code: string;
};

const FORM_ID = "giftCardForm";

const CheckoutGiftCardForm = ({
    onSubmit,
    onDelete,
    giftCardList,
    errorMessage,
    isLoading,
    renderInput,
    code
}: Props) => {
    const { t } = useGranite();
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const [active, setActive] = useState(false);
    const [errorVisible, setErrorVisible] = useState(false);
    const [hasCardCode, setHasCardCode] = useState(false);
    const [hasBonusCardPrefix, setHasBonusCardPrefix] = useState(false);
    const cart = useSelector(getCart);

    const methods = useOmniForm({
        mode: "onBlur",
        reValidateMode: "onBlur",
        defaultValues: {
            cardCode: "",
            recaptcha: "",
            bonusCard: cart?.bonusCardNumber
                ? { number: cart.bonusCardNumber }
                : undefined
        },
        resolver: yupResolver(CheckoutGiftCardFormValidationSchema)
    });

    const { subscribe, setValue, clearErrors } = methods;

    useEffect(() => {
        const callback = subscribe({
            formState: { values: true },
            callback: ({ values }) => {
                const shouldHaveBonusCardPrefix = values.cardCode.startsWith(
                    BONUSCARD_DISCOUNT_PREFIX
                );
                if (shouldHaveBonusCardPrefix !== hasBonusCardPrefix) {
                    setHasBonusCardPrefix(shouldHaveBonusCardPrefix);
                }

                const shouldHaveCardCode = values.cardCode.length > 0;
                if (shouldHaveCardCode !== hasCardCode) {
                    setHasCardCode(shouldHaveCardCode);
                }
            }
        });

        return () => callback();
    }, [subscribe, hasBonusCardPrefix, hasCardCode]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (errorMessage) {
            setErrorVisible(true);
            resetReCaptcha();
        }
    }, [errorMessage, code]);

    const resetReCaptcha = () => {
        recaptchaRef.current?.reset();
        setValue("recaptcha", "", { shouldTouch: false });
        clearErrors("recaptcha");
    };

    const onFormSubmit = (values: CheckoutGiftCardFormValues) => {
        resetReCaptcha();

        if (onSubmit) {
            if (
                !values.cardCode.startsWith(BONUSCARD_DISCOUNT_PREFIX) &&
                values.bonusCard?.number
            ) {
                delete values.bonusCard;
            }
            onSubmit(values, () => {
                methods.reset(undefined, { keepDefaultValues: true });
                scrollToTop();
            });
        }
    };

    return (
        <>
            <GiftCardList
                giftCardList={giftCardList}
                onDelete={onDelete}
            />
            {renderInput && (
                <>
                    <FormGroup
                        toggle={true}
                        className={cn("tw:pl-6")}
                    >
                        <Label>
                            <Input
                                data-test={"giftCardForm-checkbox-enable"}
                                type={"checkbox"}
                                name={"active"}
                                onChange={() => {
                                    setActive(!active);
                                    setErrorVisible(false);
                                }}
                                checked={active}
                            />{" "}
                            {t("checkout.paymentMethods.giftCard.enable")}
                        </Label>
                    </FormGroup>
                    {active && (
                        <FormInfoProvider
                            formId={FORM_ID}
                            withNeverbounce={false}
                        >
                            {() => (
                                <FormProvider {...methods}>
                                    <form
                                        id={FORM_ID}
                                        onSubmit={methods.handleSubmit(
                                            onFormSubmit
                                        )}
                                    >
                                        <h4>
                                            {t(
                                                "checkout.paymentMethods.giftCard.title"
                                            )}
                                        </h4>
                                        {errorMessage && (
                                            <Alert
                                                color="danger"
                                                fade={false}
                                                isOpen={errorVisible}
                                                closeAriaLabel={t(
                                                    "general.alert.close"
                                                )}
                                                toggle={() =>
                                                    setErrorVisible(
                                                        !errorVisible
                                                    )
                                                }
                                            >
                                                {getErrorMessage(
                                                    t,
                                                    `apiMessages.title.${errorMessage}`
                                                )}
                                            </Alert>
                                        )}
                                        <Row>
                                            <Col md={6}>
                                                <p>
                                                    {t(
                                                        "checkout.paymentMethods.giftCard.label"
                                                    )}
                                                </p>
                                                <GiftCardField<CheckoutGiftCardFormValues>
                                                    test="giftCardForm-input-cardCode"
                                                    name="cardCode"
                                                    hideLabel={true}
                                                    label={t(
                                                        "checkout.paymentMethods.giftCard"
                                                    )}
                                                    required
                                                />
                                            </Col>
                                        </Row>
                                        {hasBonusCardPrefix && (
                                            <Row>
                                                <Col md={6}>
                                                    <NumericTextField<CheckoutGiftCardFormValues>
                                                        name="bonusCard.number"
                                                        label={t(
                                                            "general.form.bonusCard.label"
                                                        )}
                                                        placeholder={t(
                                                            "general.form.bonusCard.placeholder"
                                                        )}
                                                        required={true}
                                                    />
                                                </Col>
                                            </Row>
                                        )}
                                        <div
                                            className={cn(
                                                "tw:transition-all tw:duration-400 tw:ease-in-out tw:overflow-x-hidden tw:max-h-0",
                                                hasCardCode &&
                                                    "tw:duration-800 tw:max-h-250"
                                            )}
                                        >
                                            <Row>
                                                <Col md={6}>
                                                    <RecaptchaField<CheckoutGiftCardFormValues>
                                                        ref={recaptchaRef}
                                                        name="recaptcha"
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <FormGroup>
                                                        <Button
                                                            test="giftCardForm-button-submit"
                                                            type="submit"
                                                            size="md"
                                                            loading={isLoading}
                                                        >
                                                            {t(
                                                                "checkout.paymentMethods.giftCard.button"
                                                            )}
                                                        </Button>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>
                                    </form>
                                </FormProvider>
                            )}
                        </FormInfoProvider>
                    )}
                </>
            )}
        </>
    );
};

export { CheckoutGiftCardForm };
